/**
 * Verbindung zum Gateway (Socket.IO). Ein Socket je Sitzung; Ereignisse laufen erst durch den Reducer
 * (Store) und danach an die Seiteneffekt-Handler und den Bus.
 */
import {
  SERVER_EVENT_NAMES,
  type ClientEventName,
  type ClientEventPayload,
  type ServerEventName,
  type ServerEventPayload,
} from '@lagerfeuer/shared';
import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { bindExtrasRealtime } from '../features/extras/realtime';
import { bindVoiceSocket, seedVoiceStates } from '../features/voice/voice-socket';
import { ownPasswordChangeResult, refreshAccessToken } from '../lib/api';
import { queryClient } from '../lib/queryClient';
import { createSocket, type LfSocket } from '../lib/socket';
import { appState } from '../store/app';
import { useAuthStore } from '../store/auth';
import { emitGatewayEvent, emitResync } from './bus';
import { getFocusedChannel, handleResync, handleSideEffects } from './handlers';

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'offline';

export const useConnection = create<{ state: ConnectionState; set(s: ConnectionState): void }>((set) => ({
  state: 'idle',
  set: (state) => set({ state }),
}));

let socket: LfSocket | null = null;

/** Auth-Fehlschläge beim Verbindungsaufbau in Folge (TOKEN_EXPIRED/UNAUTHORIZED trotz Refresh). */
let authFailures = 0;
let authRetryTimer: ReturnType<typeof setTimeout> | null = null;
/** Abmeldung vom Auth-Store (neues Token → sofort neu verbinden). */
let unsubscribeAuth: (() => void) | null = null;

/** Wartezeit vor dem n-ten erneuten Verbindungsversuch nach Auth-Fehler (1 s, 2 s, 4 s … max. 30 s). */
export function authRetryDelay(failures: number): number {
  return Math.min(30_000, 1000 * 2 ** Math.max(0, failures - 1));
}

function clearAuthRetry(): void {
  if (authRetryTimer) clearTimeout(authRetryTimer);
  authRetryTimer = null;
}

export function getSocket(): LfSocket | null {
  return socket;
}

function handle<E extends ServerEventName>(event: E, payload: ServerEventPayload<E>): void {
  // Erneutes READY (Neuverbindung bzw. nach dem REST-Ersatz): Zustand blieb erhalten, Lücken nachladen.
  const resync = event === 'READY' && appState().ready;
  appState().dispatch(event, payload);
  handleSideEffects(event, payload);
  emitGatewayEvent(event, payload);
  if (resync) {
    handleResync();
    emitResync();
  }
}

/**
 * Ruft `fn` bei jedem Übergang in den Zustand „verbunden" auf (z. B. CHANNEL_FOCUS/SERVER_SUBSCRIBE erneut
 * senden – der Server kennt nach einer Neuverbindung nichts mehr davon).
 */
export function useOnConnected(fn: () => void): void {
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  });
  useEffect(
    () =>
      useConnection.subscribe((s, prev) => {
        if (s.state === 'connected' && prev.state !== 'connected') ref.current();
      }),
    [],
  );
}

export function connectGateway(): LfSocket {
  if (socket) return socket;
  const s = createSocket(() => useAuthStore.getState().accessToken);
  socket = s;
  const conn = useConnection.getState();
  conn.set('connecting');

  for (const name of SERVER_EVENT_NAMES) {
    s.on(name, ((payload: never) => handle(name, payload)) as never);
  }

  // Andockstellen der Pakete Extras und Voice (eigene Listener; disconnectGateway entfernt sie mit removeAllListeners).
  bindExtrasRealtime(s, queryClient);
  bindVoiceSocket(s);
  s.on('READY', (r) => {
    seedVoiceStates(r.voiceStates);
    // Fokus ist Socket-Zustand auf dem Server: nach (Wieder-)Verbindung neu melden (R7b), sonst gäbe es Push für den
    // offenen Kanal bzw. ging die Meldung vor dem Verbinden verloren.
    const focused = getFocusedChannel();
    if (focused !== null) void gatewaySend('CHANNEL_FOCUS', { channelId: focused });
  });

  s.on('connect', () => {
    authFailures = 0;
    clearAuthRetry();
    useConnection.getState().set('connected');
  });
  s.on('disconnect', (reason) => {
    useConnection.getState().set(reason === 'io client disconnect' ? 'idle' : 'reconnecting');
    // Server hat getrennt (z. B. Rechte-/Token-Wechsel): selbst neu verbinden.
    if (reason === 'io server disconnect' && useAuthStore.getState().accessToken) s.connect();
  });
  s.on('connect_error', (err) => {
    useConnection.getState().set('reconnecting');
    const code = err.message;
    if (code === 'TOKEN_EXPIRED' || code === 'UNAUTHORIZED') {
      if (authRetryTimer) return; // Versuch bereits geplant
      authFailures++;
      // Erster Fehler (abgelaufenes Token) sofort, danach mit gedeckeltem Backoff – solange die Sitzung
      // besteht, wird nie endgültig aufgegeben (api.ts erneuert das Token ggf. später selbst).
      const delay = authFailures === 1 ? 0 : authRetryDelay(authFailures - 1);
      authRetryTimer = setTimeout(() => {
        authRetryTimer = null;
        void refreshAccessToken().then((ok) => {
          if (socket !== s) return;
          // Refresh vorübergehend fehlgeschlagen (5xx): trotzdem erneut verbinden (Backoff).
          // Ohne Sitzung (Refresh 401/403 → Token gelöscht) aufgeben – die App meldet ab.
          if (ok || useAuthStore.getState().accessToken) s.connect();
          else disconnectGateway();
        });
      }, delay);
    } else if (code === 'ACCOUNT_DISABLED') {
      disconnectGateway();
      useAuthStore.getState().clear();
    }
  });
  s.on('RECONNECT', () => {
    s.disconnect();
    void refreshAccessToken().then((ok) => ok && s.connect());
  });
  s.on('INVALID_SESSION', (p) => {
    disconnectGateway();
    // Eigener Passwortwechsel: diese Sitzung bleibt gültig (Cookie rotiert) → neues Token holen und neu verbinden.
    // Refresh-Wiederverwendung woanders (`session_reuse`): nur die betroffene Session ist widerrufen – die eigene ggf.
    // noch gültig, also per Refresh weitermachen (schlägt er fehl, wird abgemeldet).
    const own = p?.reason === 'password_changed' ? ownPasswordChangeResult() : p?.reason === 'session_reuse' ? Promise.resolve(true) : null;
    if (!own) {
      useAuthStore.getState().clear();
      return;
    }
    void own.then(async (ok) => {
      if (ok && (await refreshAccessToken())) connectGateway();
      else useAuthStore.getState().clear();
    });
  });

  // Neues Token (Refresh aus api.ts, auch nach dessen Backoff) → nicht auf den nächsten Versuch warten.
  unsubscribeAuth = useAuthStore.subscribe((st, prev) => {
    if (!st.accessToken || st.accessToken === prev.accessToken || socket !== s || s.connected) return;
    clearAuthRetry();
    s.connect();
  });

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  s.connect();
  return s;
}

function onOnline() {
  if (socket && !socket.connected) socket.connect();
}
function onOffline() {
  useConnection.getState().set('offline');
}

export function disconnectGateway(): void {
  clearAuthRetry();
  unsubscribeAuth?.();
  unsubscribeAuth = null;
  authFailures = 0;
  window.removeEventListener('online', onOnline);
  window.removeEventListener('offline', onOffline);
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
  useConnection.getState().set('idle');
}

/** Client→Server-Ereignis senden; liefert die Ack-Antwort (oder `null` ohne Verbindung/Timeout). */
export function gatewaySend<E extends ClientEventName>(event: E, payload: ClientEventPayload<E>, timeoutMs = 5000): Promise<unknown> {
  const s = socket;
  if (!s?.connected) return Promise.resolve(null);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), timeoutMs);
    (s.emit as (e: string, p: unknown, ack: (res: unknown) => void) => void)(event, payload, (res: unknown) => {
      clearTimeout(timer);
      resolve(res);
    });
  });
}
